import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { billingAPI, publicAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function CompleteSetup() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { subscriptionStatus } = useAuth();

  // Handle success redirect from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Checkout completed, clear pending data and redirect to dashboard
      localStorage.removeItem('pending_trial_plan');
      localStorage.removeItem('pending_trial_email');
      localStorage.removeItem('pending_invite_token');
      toast.success('Welcome! Your trial has started.');
      navigate('/admin');
    }
  }, [searchParams, navigate]);

  // If subscription is not incomplete, redirect to dashboard
  useEffect(() => {
    if (subscriptionStatus && subscriptionStatus !== 'incomplete') {
      navigate('/admin');
    }
  }, [subscriptionStatus, navigate]);

  const handleStartCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      const pendingPlan = localStorage.getItem('pending_trial_plan') || 'starter';
      const pendingEmail = localStorage.getItem('pending_trial_email');
      const pendingInviteToken = localStorage.getItem('pending_invite_token');

      // Get trial days from invite if exists
      let trialDays = null;
      if (pendingInviteToken) {
        try {
          const invite = await publicAPI.validateInviteToken(pendingInviteToken);
          if (invite.valid) {
            trialDays = invite.trial_days;
          }
        } catch (e) {
          console.log('Invite validation failed, using default trial');
        }
      }

      const response = await billingAPI.createSignupCheckout({
        plan: pendingPlan,
        billing_cycle: 'monthly',
        trial_days: trialDays,
        email: pendingEmail,
        success_url: `${window.location.origin}/admin/complete-setup`,
        cancel_url: `${window.location.origin}/admin/complete-setup`,
      });

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        background: 'linear-gradient(135deg, #E8F5F5 0%, #FFFFFF 50%, #E8F5F5 100%)',
      }}
    >
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Smart Assistant™ Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-cleona-600 px-4 py-2 mb-6">
            <span className="text-white font-manrope font-semibold text-sm">Smart Assistant™</span>
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white">
              <svg className="w-3.5 h-3.5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </span>
          </div>

          <h1 className="font-jakarta text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Complete Your Setup
          </h1>
          <p className="text-gray-600 font-manrope">
            Add your payment method to start your free trial.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Benefits */}
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-cleona-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-manrope font-medium text-gray-900">14-day free trial</p>
                <p className="text-sm text-gray-500">Full access to all features</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-cleona-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p className="font-manrope font-medium text-gray-900">Cancel anytime</p>
                <p className="text-sm text-gray-500">No questions asked</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-cleona-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-cleona-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <p className="font-manrope font-medium text-gray-900">You won't be charged today</p>
                <p className="text-sm text-gray-500">Billing starts after trial ends</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm font-manrope">
              {error}
            </div>
          )}

          <button
            onClick={handleStartCheckout}
            disabled={loading}
            className="w-full rounded-xl bg-cleona-600 px-6 py-4 text-base font-semibold text-white hover:bg-cleona-700 transition-colors font-manrope disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : (
              'Start Free Trial'
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4 font-manrope">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-6 font-manrope">
          Secure payment powered by Stripe
        </p>
      </div>
    </div>
  );
}
